import { useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { readShared } from "../filters";
import { TabLink } from "../links";
import type { SourcingTab } from "../routes";
import { useFilterOptions } from "../SharedFilterBar";
import { Note, PageHead } from "./kit";

export interface PlainItem {
  item_id: string;
  item_name: string;
  funnel: string;
}

/**
 * The chosen item when it has no variant to show: its offers carry none, so the
 * four variant pages have nothing to draw. Null while the list is loading, when
 * no item is chosen, or when the item has variants.
 */
export function plainItem(
  item: string | null,
  options: { items: PlainItem[]; variants: string[] } | null,
): PlainItem | null {
  if (!item || !options || options.variants.length > 0) return null;
  return options.items.find((candidate) => candidate.item_id === item) ?? null;
}

export function IngredientsOnly({ item }: { item: PlainItem }) {
  return (
    <Note>
      <b>Ingredients only.</b> {item.item_name} is a {item.funnel} item, and its offers have no variant, so this page has
      nothing to show. Use <TabLink tabKey="sourcing_funnel">Sourcing Funnel</TabLink> for its cost.
    </Note>
  );
}

/** Wraps a variant page: an item without variants gets the head and the note in place of the page. */
export function VariantPage({ tab, children }: { tab: SourcingTab; children: ReactNode }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const item = readShared(search).item;
  const options = useFilterOptions(item);
  const plain = plainItem(item, options.data?.data ?? null);
  if (plain) {
    return (
      <>
        <PageHead title={tab.label} />
        <IngredientsOnly item={plain} />
      </>
    );
  }
  return <>{children}</>;
}
